import type { Notification } from "./types";
import { normalizeType, resolveVariantKey } from "./content";
import { getCurrencySymbol } from "@/shared/lib";

/**
 * Display-copy layer — turns a raw backend notification into the variables
 * a friendly, consistent copy template needs. The templates themselves live
 * in the locale files under `notifications.copy.<TYPE>.{title,body}` so
 * they translate like any other UI string; this module only extracts and
 * formats the interpolation variables (pure data — no JSX, no i18n).
 *
 * A widget-level hook (useNotificationCopy) marries the two: it renders the
 * template when every required variable is present, and falls back to the
 * backend-provided title/body otherwise — so a notification never renders
 * with a hole in its sentence.
 */

export type NotificationCopyVars = Record<string, string | number | undefined>;

/** Words kept lowercase when humanizing enum-ish codes (unless first). */
const SMALL_WORDS = new Set(["for", "of", "and", "the", "to", "in", "on", "at", "by", "a", "an"]);

/** "READY_FOR_SHIPMENT" → "Ready for Shipment"; "BUSINESS_PRO" → "Business Pro". */
export function humanizeCode(code: unknown): string | undefined {
  if (typeof code !== "string" || !code.trim()) return undefined;
  return code
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w, i) =>
      i > 0 && SMALL_WORDS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ");
}

function formatAmount(value: unknown, currency: unknown): string | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const n = Number(value);
  if (Number.isNaN(n)) return undefined;
  const code = typeof currency === "string" && currency ? currency : "NPR";
  return `${getCurrencySymbol(code.toUpperCase())} ${n.toLocaleString()}`;
}

/** details.orderNumber first; else fish an ORD-… token out of title/body so
 *  order-scoped copy still works for payloads that only embed the number in
 *  the backend sentence (e.g. ORDER_CREATED's example payload). */
function extractOrderNumber(n: Notification): string | undefined {
  const direct = n.data?.details?.orderNumber;
  if (typeof direct === "string" && direct.trim()) return direct.replace(/^#/, "");
  const m = `${n.title ?? ""} ${n.body ?? ""}`.match(/#?\b(ORD[A-Za-z0-9-]*\d)/i);
  return m ? m[1] : undefined;
}

/** "Shoes, Pants" or "Shoes, Pants, +10 more items". */
function buildItemsSummary(n: Notification): string | undefined {
  const details = (n.data?.details ?? {}) as Record<string, unknown>;
  const items = Array.isArray(details.items) ? details.items : [];
  let names = items
    .map((it) =>
      it && typeof it === "object" ? (it as { title?: unknown }).title : undefined,
    )
    .filter((t): t is string => typeof t === "string" && t.trim().length > 0);

  // Item cancellations don't populate `details.items` — the removed lines
  // ship under `details.cancelledItems`, keyed by `name` rather than `title`.
  if (names.length === 0) {
    const cancelledItems = Array.isArray(details.cancelledItems)
      ? (details.cancelledItems as unknown[])
      : [];
    names = cancelledItems
      .map((it) => (it && typeof it === "object" ? (it as { name?: unknown }).name : undefined))
      .filter((t): t is string => typeof t === "string" && t.trim().length > 0);
  }

  if (names.length === 0) return undefined;
  const count =
    typeof details?.itemsCount === "number" ? details.itemsCount : names.length;
  const shown = names.slice(0, 2).join(", ");
  const more = count - Math.min(2, names.length);
  return more > 0 ? `${shown}, +${more} more items` : shown;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

/** details.fromStatus/toStatus first; else recover the pair from backend
 *  body shapes like "CONFIRMED -> PROCESSING" or "Reverted from CONFIRMED
 *  to PROCESSING" — real status-change payloads often ship without details. */
function extractStatusPair(n: Notification): {
  fromStatus?: string;
  toStatus?: string;
} {
  const d = n.data?.details ?? {};
  let from = str(d.fromStatus);
  let to = str(d.toStatus);
  if (!from || !to) {
    const body = n.body ?? "";
    const m =
      body.match(/\b([A-Z][A-Z0-9_]+)\s*(?:->|→)\s*([A-Z][A-Z0-9_]+)/) ??
      body.match(/\bfrom\s+([A-Z][A-Z0-9_]+)\s+to\s+([A-Z][A-Z0-9_]+)/i);
    if (m) {
      from = from ?? m[1];
      to = to ?? m[2];
    }
  }
  return { fromStatus: humanizeCode(from), toStatus: humanizeCode(to) };
}

function num(value: unknown): number | undefined {
  const n = Number(value);
  return value === null || value === undefined || value === "" || Number.isNaN(n)
    ? undefined
    : n;
}

/** Extract every interpolation variable any copy template may reference. */
export function buildCopyVars(n: Notification): NotificationCopyVars {
  const d = n.data?.details ?? {};
  const currency = d.currency;

  const trackingCode = str(d.trackingCode) ?? str((d as Record<string, unknown>).trackingNumber);
  const refundAmount = formatAmount((d as Record<string, unknown>).refundAmount, currency);
  const { fromStatus, toStatus } = extractStatusPair(n);

  let planName =
    str((d as Record<string, unknown>).planName) ??
    humanizeCode((d as Record<string, unknown>).planCode);
  let toPlan =
    humanizeCode(d.toPlanCode) ?? str((d as Record<string, unknown>).planName);
  // Subscription payloads often carry the plan only in the backend title
  // ("Subscription update: Free Trial") — recover it from the colon tail.
  if (normalizeType(n.type).startsWith("SUBSCRIPTION")) {
    const tail = str((n.title ?? "").match(/:\s*(.+?)\s*$/)?.[1]);
    planName = planName ?? tail;
    toPlan = toPlan ?? tail;
  }

  return {
    orderNumber: extractOrderNumber(n),
    itemsSummary: buildItemsSummary(n),
    itemsCount: num(d.itemsCount),
    amount: formatAmount(d.totalAmount, currency),
    fromStatus,
    toStatus,
    statusLabel: str(d.statusLabel),
    // Optional sentence tails — empty string when absent so templates can
    // append them unconditionally without leaving a dangling gap.
    trackingSuffix: trackingCode ? ` Tracking Number: ${trackingCode}.` : "",
    refundSuffix: refundAmount ? ` of ${refundAmount}` : "",
    orderCount: num((d as Record<string, unknown>).orderCount),
    collectedAmount: formatAmount(d.collectedAmount, currency),
    expectedAmount: formatAmount(d.expectedAmount, currency),
    // Returns / cancellations
    returnNumber: str((d as Record<string, unknown>).returnNumber),
    returnItemsSummary: str((d as Record<string, unknown>).returnItemsSummary),
    returnReason: str((d as Record<string, unknown>).returnReason),
    refundAmount,
    pendingDays: num((d as Record<string, unknown>).pendingDays),
    amountDue: formatAmount((d as Record<string, unknown>).amountDue, currency),
    name:
      str((d as Record<string, unknown>).acceptedUserName) ??
      str((d as Record<string, unknown>).name),
    planName,
    toPlan,
    daysLeft: num((d as Record<string, unknown>).daysLeft),
    // Service-type document verification lifecycle
    storeName: str((d as Record<string, unknown>).storeName),
    rejectionReason: str((d as Record<string, unknown>).rejectionReason),
  };
}

/**
 * Copy key for a notification — normalized type, plus audience/outcome
 * variants. Delegates to resolveVariantKey (content.ts) so copy, icon and
 * tone all resolve from the same key and never drift out of sync.
 */
export function resolveCopyKey(n: Notification): string {
  return resolveVariantKey(n);
}

/**
 * Resolve the display copy for a notification: the locale template at
 * `notifications.copy.<TYPE>.{title,body}` rendered with the extracted
 * variables, falling back to the backend-provided title/body when a required
 * variable is missing or the type has no template. Pure — takes `t` as a
 * parameter so it serves both the widget hook (useTranslation) and
 * non-component call sites like the realtime toast (i18n singleton).
 */
export function resolveNotificationCopy(
  n: Notification,
  t: (key: string, options?: Record<string, unknown>) => string,
): { title: string; body: string } {
  const vars = buildCopyVars(n);
  const key = resolveCopyKey(n);
  const required = REQUIRED_COPY_VARS[key] ?? [];
  const missing = required.some(
    (r) => vars[r] === undefined || vars[r] === null || vars[r] === "",
  );
  if (missing) return { title: n.title, body: n.body };
  return {
    title: t(`notifications.copy.${key}.title`, {
      ...vars,
      defaultValue: n.title,
    }),
    body: t(`notifications.copy.${key}.body`, {
      ...vars,
      defaultValue: n.body,
    }),
  };
}

/**
 * Variables that MUST resolve for a template to render. If any is missing
 * the UI falls back to the backend title/body rather than rendering a
 * sentence with a hole in it. Types absent from this map have fully static
 * copy and always render.
 */
export const REQUIRED_COPY_VARS: Record<string, string[]> = {
  ORDER_CREATED: ["orderNumber", "itemsSummary", "amount"],
  ORDER_CANCELLED: ["orderNumber"],
  ORDER_CANCELLED_BY_CUSTOMER: ["orderNumber"],
  ORDER_ITEM_CANCELLED: ["orderNumber", "itemsSummary", "amountDue"],
  ORDER_ITEM_CANCELLED_BY_CUSTOMER: ["orderNumber", "itemsSummary", "amountDue"],
  ORDER_DELIVERED: ["orderNumber"],
  ORDER_SHIPPED: ["orderNumber"],
  ORDER_STATUS_CHANGED: ["orderNumber", "toStatus"],
  ORDER_STATUS_REVERTED: ["orderNumber", "fromStatus", "toStatus"],
  ORDER_PAYMENT_FAILED: ["orderNumber"],
  ORDER_REFUND_ISSUED: ["orderNumber"],
  SHIPMENT_CREATED: ["orderNumber"],
  SHIPMENT_DELIVERY_FAILED: ["orderNumber"],
  SHIPMENT_PICKUP_SCHEDULED: ["orderCount"],
  SHIPMENT_RETURNED: ["orderNumber"],
  LOGISTICS_COMMENT_ADDED: ["orderNumber"],
  LOGISTICS_STATUS_CHANGED: ["orderNumber", "statusLabel"],
  CASH_TRANSACTION_DISCREPANCY_CREATED: [
    "orderNumber",
    "collectedAmount",
    "expectedAmount",
  ],
  CASH_TRANSACTION_DISCREPANCY_RESOLVED: ["orderNumber"],
  RETURN_REQUESTED: ["orderNumber", "returnItemsSummary", "returnReason"],
  RETURN_COMPLETED_REFUND_DUE: ["returnNumber", "orderNumber", "refundAmount"],
  RETURN_COMPLETED_NO_REFUND: ["returnNumber", "orderNumber"],
  RETURN_EXPIRED: ["returnNumber", "orderNumber", "returnItemsSummary"],
  RETURN_CANCELLED_AFTER_APPROVAL: ["returnNumber", "orderNumber"],
  RETURN_CANCELLED: ["orderNumber"],
  ORDER_REFUND_OVERDUE: ["orderNumber", "refundAmount", "pendingDays"],
  INVITE_ACCEPTED: ["name"],
  SUBSCRIPTION_ACTIVATED: ["planName"],
  SUBSCRIPTION_UPGRADED: ["toPlan"],
  SUBSCRIPTION_TRIAL_ENDING_SOON: ["daysLeft"],
  SERVICE_TYPE_DOCUMENT_SUBMITTED: ["storeName"],
  SERVICE_TYPE_DOCUMENT_REJECTED: ["rejectionReason"],
};
